import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from '../pages/auth/apiSlice';
import moment from 'moment';
import { MicroserviceTypes } from 'typings';
import {
  brandApi,
  clientApi,
  countryApi,
  dealsApi,
  itemApi,
  leadsApi,
  ordersApi,
  purchaseOrderApi,
  purchaseRequestApi,
  supplierApi,
  taskApi,
  usersApi,
} from 'api';
import { convertStringsToOptionsObject } from 'utils/helpers';
import { CLIENT_EMAIL_ATTACHMENT_TYPE } from 'constants/index';

export interface ILogContent {
  content: any[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export interface LogQueryArgs {
  page?: number;
  size?: number;
  service?: string;
  entityName: string;
  filters: any;
  id: string;
  isAdminLogs?: boolean;
}

export interface CommentQueryArgs {
  page?: number;
  size?: number;
  id: string;
}

export const globalApi = createApi({
  tagTypes: [
    'COUNTRY_LIST',
    'LANGUAGE_LIST',
    'Tag',
    'ATTACHMENT_LIST',
    'CONTACT_PERSON',
    'COMMENT_LIST',
    'PURCHASE_REQUEST',
    'PURCHASE_REQUEST_LIST',
  ],
  reducerPath: 'globalApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    country: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/country',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.commonName,
          states: item.states?.map((_state: string) => ({
            value: _state,
            label: _state,
          })),
        }));
      },
      providesTags: () => ['COUNTRY_LIST'],
    }),
    countrySelectList: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/country/selector-list',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.commonName,
          states: item.states?.map((_state: string) => ({
            value: _state,
            label: _state,
          })),
        }));
      },
      providesTags: () => ['COUNTRY_LIST'],
    }),
    countryById: builder.query<any, Record<'countryId', string>>({
      query: ({ countryId }) => ({
        url: `/wrd/api/country/${countryId}`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response?.data,
          states: response?.data.states.map((_state: string) => ({
            value: _state,
            label: _state,
          })),
        };
      },
    }),
    language: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/language',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
      providesTags: () => ['LANGUAGE_LIST'],
    }),
    languageSelectList: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/language/selector-list',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
      providesTags: () => ['LANGUAGE_LIST'],
    }),
    regionsList: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/region',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
          subRegionSelectorList: item.subRegion.map((subRegion: string) => ({ label: subRegion, value: subRegion })),
        }));
      },
      providesTags: () => ['LANGUAGE_LIST'],
    }),
    subRegionsList: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/region/sub-region',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          value: item,
          label: item,
        }));
      },
      providesTags: () => ['LANGUAGE_LIST'],
    }),
    paymentMethod: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/payment-method',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
    }),
    industry: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/industry',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
    }),
    logList: builder.infiniteQuery<ILogContent, LogQueryArgs, number>({
      infiniteQueryOptions: {
        // Must provide a default initial page param value
        initialPageParam: 0,
        // Optionally limit the number of cached pages
        maxPages: 3,
        // Must provide a `getNextPageParam` function
        getNextPageParam: (lastPage, allPages, lastPageParam, _allPageParams) => {
          return lastPageParam < lastPage.totalPages - 1 ? lastPageParam + 1 : undefined;
        },
        // Optionally provide a `getPreviousPageParam` function
        getPreviousPageParam: (firstPage, allPages, firstPageParam, _allPageParams) => {
          return firstPageParam > 0 ? firstPageParam - 1 : undefined;
        },
      },
      // The `query` function receives `{queryArg, pageParam}` as its argument
      query: ({ queryArg, pageParam }) => {
        const { id, isAdminLogs, filters, entityName, size, service = 'wrd' } = queryArg;
        const filterParam = Object.values(filters).filter((i) => !!i)?.length ? filters : undefined;
        return {
          url: isAdminLogs ? `/wrd/api/logs/created-by/${id}` : `/${service}/api/logs/${id}`,
          params: {
            page: pageParam,
            size,
            entityName,
            ...filterParam,
          },
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content?.[0];
        if (!content) {
          return response?.data;
        }
        const { logs = {}, managers = {} } = content;

        if (Object.keys(logs || {}).length) {
          Object.values(logs || {})
            .flat()
            .forEach((log: any) => {
              log.user = managers[log.userId];
            });
        }
        return response.data;
      },
    }),
    // Attachment
    attachmentCreate: builder.mutation<any, any>({
      query: (body: { entityId: string; entityName: string; service?: MicroserviceTypes }) => {
        return {
          url: `/${body.service || 'wrd'}/api/attachments`,
          method: 'post',
          body: { ...body, service: undefined },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM', 'ITEM_LIST']));
        dispatch(countryApi.util.invalidateTags(['COUNTRY', 'COUNTRY_LIST']));
        dispatch(taskApi.util.invalidateTags(['TASK', 'TASK_LIST']));
        dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
        dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
        dispatch(usersApi.util.invalidateTags(['USER', 'USER_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        dispatch(ordersApi.util.invalidateTags(['ORDER', 'ORDER_LIST']));
      },
    }),
    attachmentBulkCreate: builder.mutation<any, any>({
      query: (args: {
        attachments: Array<{ entityId: string; entityName: string; attachmentType: string }>;
        service?: MicroserviceTypes;
      }) => {
        return {
          url: `/${args.service || 'wrd'}/api/attachments/bulk`,
          method: 'post',
          body: args.attachments,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM', 'ITEM_LIST']));
        dispatch(countryApi.util.invalidateTags(['COUNTRY', 'COUNTRY_LIST']));
        dispatch(taskApi.util.invalidateTags(['TASK', 'TASK_LIST']));
        dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
        dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
        dispatch(usersApi.util.invalidateTags(['USER', 'USER_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        dispatch(ordersApi.util.invalidateTags(['ORDER', 'ORDER_LIST']));
      },
    }),
    attachmentList: builder.query<any, any>({
      query: (args) => {
        const { entityName, entityId, size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }), entityName };
        return {
          url: `/${args.service || 'wrd'}/api/attachments/${entityId}/list?size=${size}&page=${number}${sortParam}`,
          params: params,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = (response?.data?.content || []).map((_item: any) => ({
          ..._item,
          formattedCreateDate: {
            full: _item.createDate ? moment(_item.createDate).format('DD.MM.YYYY HH:mm:ss') : '',
            day: _item.createDate ? moment(_item.createDate).format('DD.MM.YYYY') : '',
          },
          formattedLastModifiedDate: {
            full: _item.lastModifiedDate ? moment(_item.createDate).format('DD.MM.YYYY HH:mm:ss') : '',
            day: _item.lastModifiedDate ? moment(_item.lastModifiedDate).format('DD.MM.YYYY') : '',
          },
        }));
        return response.data;
      },
      providesTags: () => ['ATTACHMENT_LIST'],
    }),
    attachmentUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/${body.service || 'wrd'}/api/attachments`,
          method: 'put',
          body: { ...body, service: undefined },
        };
      },
      invalidatesTags: () => ['ATTACHMENT_LIST'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM', 'ITEM_LIST']));
        dispatch(countryApi.util.invalidateTags(['COUNTRY', 'COUNTRY_LIST']));
        dispatch(taskApi.util.invalidateTags(['TASK', 'TASK_LIST']));
        dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
        dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
        dispatch(usersApi.util.invalidateTags(['USER', 'USER_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        dispatch(ordersApi.util.invalidateTags(['ORDER', 'ORDER_LIST']));
      },
    }),
    attachmentDelete: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/${body.service || 'wrd'}/api/attachments`,
          method: 'delete',
          body: { ...body, service: undefined },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // const entity: Record<string, any> = {
        //   BRAND: {
        //     api: brandApi,
        //     tags: ['BRAND', 'BRAND_LIST'],
        //   },
        //   ADMIN: {
        //     api: usersApi,
        //     tags: ['USER'],
        //   },
        //   SUPPLIER: {
        //     api: supplierApi,
        //     tags: ['SUPPLIER'],
        //   },
        //   TASK: {
        //     api: taskApi,
        //     tags: ['TASK', 'TASK_LIST'],
        //   },
        //   LEAD: {
        //     api: leadsApi,
        //     tags: ['LEAD', 'LEAD_LIST'],
        //   },
        //   DEAL: {
        //     api: dealsApi,
        //     tags: ['DEAL', 'DEAL_LIST'],
        //   },
        //   ITEM: {
        //     api: itemApi,
        //     tags: ['ITEM', 'ITEM_LIST'],
        //   },
        // };
        // dispatch(entity[arg.entityName].api.util.invalidateTags(entity[arg.entityName].tags));

        dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM', 'ITEM_LIST']));
        dispatch(countryApi.util.invalidateTags(['COUNTRY', 'COUNTRY_LIST']));
        dispatch(taskApi.util.invalidateTags(['TASK', 'TASK_LIST']));
        dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
        dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
        dispatch(usersApi.util.invalidateTags(['USER', 'USER_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        dispatch(ordersApi.util.invalidateTags(['ORDER', 'ORDER_LIST']));
      },
      invalidatesTags: () => ['ATTACHMENT_LIST'],
    }),
    attachmentType: builder.query<any, any>({
      query: ({ entityName, service = 'wrd' }) => ({
        url: `/${service}/api/attachments/attachment-type-list/${entityName}`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = response?.data?.filter((i: string) => i !== CLIENT_EMAIL_ATTACHMENT_TYPE) || [];
        return convertStringsToOptionsObject(response.data);
      },
    }),
    timeZoneList: builder.query<any, void>({
      query: () => ({
        url: `/uaa/api/time-zones`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          value: item,
          label: item,
        }));
      },
    }),
    // Contact Person
    contactPersonCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/contact-person',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const tags: any = arg.entity === 'SUPPLIER' ? ['SUPPLIER'] : ['CLIENT'];
        dispatch((arg.entity === 'SUPPLIER' ? supplierApi : clientApi).util.invalidateTags(tags));
        dispatch(dealsApi.util.invalidateTags(['DEAL']));
      },
      invalidatesTags: () => ['CONTACT_PERSON'],
    }),
    contactPersonRead: builder.query<any, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/wrd/api/contact-person/get-by-entityId?entityId=${entityId}`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((i: any) => ({
          ...i,
          value: i.id,
          label: i.fullName,
        }));
      },
      providesTags: () => ['CONTACT_PERSON'],
    }),
    contactPersonUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/contact-person',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const tags: any = arg.entity === 'SUPPLIER' ? ['SUPPLIER'] : ['CLIENT'];
        dispatch((arg.entity === 'SUPPLIER' ? supplierApi : clientApi).util.invalidateTags(tags));
      },
      invalidatesTags: () => ['CONTACT_PERSON'],
    }),
    contactPersonDelete: builder.mutation<any, { id: string; entity: string }>({
      query: ({ id }) => ({
        url: `/wrd/api/contact-person?id=${id}`,
        method: 'delete',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const tags: any = arg.entity === 'SUPPLIER' ? ['SUPPLIER'] : ['CLIENT'];
        dispatch((arg.entity === 'SUPPLIER' ? supplierApi : clientApi).util.invalidateTags(tags));
      },
      invalidatesTags: () => ['CONTACT_PERSON'],
    }),

    commentCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/comment',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(itemApi.util.invalidateTags(['ITEM', 'ITEM_LIST']));
        dispatch(countryApi.util.invalidateTags(['COUNTRY', 'COUNTRY_LIST']));
        dispatch(taskApi.util.invalidateTags(['TASK', 'TASK_LIST']));
        dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
        dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
        dispatch(usersApi.util.invalidateTags(['USER', 'USER_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
      },
      invalidatesTags: () => ['COMMENT_LIST'],
    }),
    commentList: builder.infiniteQuery<ILogContent, CommentQueryArgs, number>({
      infiniteQueryOptions: {
        // Must provide a default initial page param value
        initialPageParam: 0,
        // Optionally limit the number of cached pages
        maxPages: 3,
        // Must provide a `getNextPageParam` function
        getNextPageParam: (lastPage, allPages, lastPageParam, _allPageParams) => {
          return lastPageParam < lastPage.totalPages - 1 ? lastPageParam + 1 : undefined;
        },
        // Optionally provide a `getPreviousPageParam` function
        getPreviousPageParam: (firstPage, allPages, firstPageParam, _allPageParams) => {
          return firstPageParam > 0 ? firstPageParam - 1 : undefined;
        },
      },
      // The `query` function receives `{queryArg, pageParam}` as its argument
      query: ({ queryArg, pageParam }) => {
        const { id, size } = queryArg;
        return {
          url: `/wrd/api/comment/${id}`,
          params: {
            page: pageParam,
            size,
          },
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content?.[0];
        if (!content) {
          return response?.data;
        }
        const { comments = {}, managers = {} } = content;

        if (Object.keys(comments || {}).length) {
          Object.values(comments || {})
            .flat()
            .forEach((comment: any) => {
              comment.user = managers[comment.userId];
            });
        }
        return response.data || {};
      },
      providesTags: () => ['COMMENT_LIST'],
    }),
  }),
});

export const {
  useCountryQuery,
  useCountrySelectListQuery,
  useCountryByIdQuery,
  useLanguageQuery,
  useLanguageSelectListQuery,
  usePaymentMethodQuery,
  useIndustryQuery,
  useRegionsListQuery,
  useSubRegionsListQuery,
  useAttachmentCreateMutation,
  useAttachmentBulkCreateMutation,
  useAttachmentListQuery,
  useAttachmentUpdateMutation,
  useAttachmentDeleteMutation,
  useAttachmentTypeQuery,
  useTimeZoneListQuery,
  useContactPersonCreateMutation,
  useContactPersonReadQuery,
  useContactPersonUpdateMutation,
  useContactPersonDeleteMutation,
  useCommentCreateMutation,
  useCommentListInfiniteQuery,
  useLogListInfiniteQuery,
} = globalApi;
